import { Box, Stack } from '@mui/material'
import { ConditionTypes } from 'components/core/condition-builder/C1ConditionBuilder'
import { ToggleViewButtons } from 'components/core/condition-builder/ToggleViewButtons'
import { C1CELEditor } from 'components/core/RHKFormComponents/C1CELEditor'
import { Expression, parseExpression } from 'components/pages/dynamic_groups/detail/expression-builder/expressionParser'
import { ExpressionPartReadOnly } from 'components/pages/dynamic_groups/detail/expression-builder/ExpressionPartReadOnly'
import { EditorValidationEnv } from 'pbts/c1/api/editor/v1/editor'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'

type BasicExpressionReadOnlyProps = {
    expression: string
    muteError?: boolean
}

export const C1DisplayCondition = ({ expression }: BasicExpressionReadOnlyProps): ReactNode => {
    const [expressions, setExpressions] = useState<Array<Expression | Expression[]>>([])
    const [showForceSwitchMessage, setShowForceSwitchMessage] = useState(false)
    const [conditionType, setConditionType] = useState<ConditionTypes>('basic')

    useEffect(() => {
        try {
            setExpressions(parseExpression(expression))

            if (expressions.length > 0) {
                setShowForceSwitchMessage(false)
                setConditionType('basic')
            } else {
                setConditionType('cel')
                setShowForceSwitchMessage(true)
            }
        } catch (err) {
            setConditionType('cel')
            setShowForceSwitchMessage(true)
        }
    }, [expression, expressions.length])

    const toggleViewOptions = useMemo(() => [{
        value: 'basic',
        label: <FormattedMessage defaultMessage="Basic" id="expressionBuilder.basic" />,
        disabled: showForceSwitchMessage,
        view: <Stack spacing={2} direction="column" flex={1} mt={2}>
            {expressions.map((nestedExprs, i) => (
                <Box key={`expression-${i}`}>
                    {Array.isArray(nestedExprs)
                        ? <Stack
                            direction="row"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={3}
                            sx={{
                                backgroundColor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'border.primary',
                                borderRadius: 1,
                                p: 3,
                            }}
                        >
                            {nestedExprs.map((nestedExpr) => <Box key={nestedExpr.id}>
                                <ExpressionPartReadOnly expression={nestedExpr} />
                            </Box>)}
                        </Stack>
                        : <ExpressionPartReadOnly
                            expression={nestedExprs}
                            key={nestedExprs.id}
                        />
                    }
                </Box>
            ))}
        </Stack>,
    },
    {
        value: 'cel',
        label: <FormattedMessage defaultMessage="Expression" id="expressionBuilder.expression" />,
        view: <Box mt={2}>
            <C1CELEditor
                value={expression}
                defaultLanguage="cel"
                copilot
                validationEnv={EditorValidationEnv.POLICY}
                options={{ readOnly: true, domReadOnly: true, lineNumbers: 'off' }}
            />
        </Box>,
    }], [expression, expressions, showForceSwitchMessage])

    return <ToggleViewButtons
        value={conditionType}
        aria-label="Switch condition view"
        onChange={(v): void => setConditionType(v as ConditionTypes)}
        options={toggleViewOptions}
        disabled={showForceSwitchMessage}
        tooltip={showForceSwitchMessage
            ? <FormattedMessage defaultMessage="Custom conditions must be viewed as an expression" id="celDrawer.forceSwitchMessage" />
            : undefined}
        label={conditionType === 'basic'
            ? <FormattedMessage defaultMessage="Users who have" id="basic_expression_users_who_have" />
            : <FormattedMessage defaultMessage="Users who match expression" id="basic_expression_users_who_match" />
        }
    />
}
